import React, { useState, useEffect, useRef, useMemo } from 'react';
import Style from 'ol/style/Style';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { useUIStore, useCategoriesStore, useFincasStore, useGlobalStore } from '../../stores';
import { useUnifiedElementsStore } from '../../stores/unifiedElementsStore';
import { MapService } from '../../services/map/mapService';
import { RecintosReportService } from '../../services/reports/recintosReportService';
import { logger } from '../../../../shared/utils/logger';
// Nota: Evitamos ambigüedad con otras variantes comprobando directamente el id
// 'ZC' para zonas comunes en V2.
import './ElementsTablePanel.css';

interface ElementsTablePanelProps {
  mapService: MapService | null;
}

// Tipos para el ordenamiento
type SortColumn = 'elemId' | 'catId' | 'nombre' | 'categoria' | 'area' | 'computo' | 'finca' | 'nombreFinca' | 'refcat';
type SortDirection = 'asc' | 'desc';

const ElementsTablePanel: React.FC<ElementsTablePanelProps> = ({ mapService }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localSelectedStoreyId, setLocalSelectedStoreyId] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('elemId');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [panelHeight, setPanelHeight] = useState<number>(400); // Altura inicial del panel
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  
  const { addNotification } = useUIStore();
  const { getCategoryById, getCategoryLabel, getCategoryColor, initializeCategories } = useCategoriesStore();
  const { getAllFincas } = useFincasStore();
  const { selectedStoreyId, setSelectedStoreyId } = useGlobalStore();
  const { 
    elements, 
    selectedElementId, 
    setMapService, 
    getElementsByStorey
  } = useUnifiedElementsStore();

  // Función para obtener el nombre de una finca por su número
  const getFincaNombreByNumero = (numeroFinca: number | null | string): string => {
    if (!numeroFinca) return 'Sin asignar';
    
    // Convertir a number si es string
    const numeroFincaNum = typeof numeroFinca === 'string' ? parseInt(numeroFinca) : numeroFinca;
    if (isNaN(numeroFincaNum)) return 'Sin asignar';
    
    const fincas = getAllFincas();
    const finca = fincas.find(f => f.numeroFinca === numeroFincaNum);
    
          if (finca) {
        const nombreMostrado = finca.nombre || `Finca ${finca.numeroFinca}`;
        logger.log('ELEMENTS_TABLE_PANEL', `🏠 ElementsTablePanel: Finca ${numeroFincaNum} -> "${nombreMostrado}" (nombre personalizado: "${finca.nombre}")`);
        return nombreMostrado;
      } else {
        logger.warn('ELEMENTS_TABLE_PANEL', `⚠️ ElementsTablePanel: No se encontró finca con número ${numeroFincaNum}`);
        return `Finca ${numeroFincaNum}`;
      }
  };

  // Función para ordenar elementos
  const sortElements = (elements: any[], column: SortColumn, direction: SortDirection) => {
    return [...elements].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (column) {
        case 'elemId':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'catId':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'nombre':
          aValue = (a.nombre || '').toLowerCase();
          bValue = (b.nombre || '').toLowerCase();
          break;
        case 'categoria':
          aValue = getCategoryLabel(a.category).toLowerCase();
          bValue = getCategoryLabel(b.category).toLowerCase();
          break;
        case 'area':
          aValue = a.area;
          bValue = b.area;
          break;
        case 'computo':
          aValue = getCategoryById(a.category)?.computo || 0;
          bValue = getCategoryById(b.category)?.computo || 0;
          break;
        case 'finca':
          aValue = a.finca || '';
          bValue = b.finca || '';
          break;
        case 'nombreFinca':
          aValue = getFincaNombreByNumero(a.finca).toLowerCase();
          bValue = getFincaNombreByNumero(b.finca).toLowerCase();
          break;
        case 'refcat':
          aValue = a.refcat || '';
          bValue = b.refcat || '';
          break;
        default:
          aValue = a[column];
          bValue = b[column];
      }

      // Manejar valores nulos/undefined
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Comparación
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  };

  // Función para manejar el clic en el header de la tabla
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Si es la misma columna, cambiar dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es una columna diferente, establecer como nueva columna de ordenamiento
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Función para obtener el icono de ordenamiento
  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return (
        <svg className="v2-elements-table-sort-icon" viewBox="0 0 24 24">
          <path d="M7,10L12,15L17,10H7Z" />
        </svg>
      );
    }
    
    return (
      <svg className={`v2-elements-table-sort-icon ${sortDirection}`} viewBox="0 0 24 24">
        {sortDirection === 'asc' ? (
          <path d="M7,14L12,9L17,14H7Z" />
        ) : (
          <path d="M7,10L12,15L17,10H7Z" />
        )}
      </svg>
    );
  };

  // Elementos filtrados por planta y ordenados (memoizados)
  const filteredAndSortedElements = useMemo(() => {
    if (!localSelectedStoreyId) return [];
    const elementsForStorey = getElementsByStorey(localSelectedStoreyId);
    logger.log('ELEMENTS_TABLE_PANEL', `📊 ElementsTablePanel: ${elementsForStorey.length} elementos para planta ${localSelectedStoreyId}`);
    
    // Ordenar los elementos
    const sortedElements = sortElements(elementsForStorey, sortColumn, sortDirection);
    return sortedElements;
  }, [localSelectedStoreyId, elements, getElementsByStorey, sortColumn, sortDirection]);



  // Inicializar categorías y configurar MapService al montar el componente
  useEffect(() => {
    initializeCategories();
    
    // Configurar MapService en el store de elementos
    setMapService(mapService);
    
    // Refrescar estilos de los dibujos existentes después de inicializar categorías
    if (mapService) {
      setTimeout(() => {
        // Verificar que el método existe antes de llamarlo
        if (typeof mapService.refreshDrawLayerStyles === 'function') {
          mapService.refreshDrawLayerStyles();
        } else {
          console.warn('⚠️ ElementsTablePanel: refreshDrawLayerStyles no está disponible, usando método manual');
          manualRefreshStyles();
        }
      }, 500); // Pequeño delay para asegurar que el store esté inicializado
    }
  }, [initializeCategories, mapService, setMapService]);

  // Efecto para actualizar la planta local cuando cambia la planta global
  useEffect(() => {
    if (selectedStoreyId && selectedStoreyId !== localSelectedStoreyId) {
      logger.log('ELEMENTS_TABLE_PANEL', `🔄 ElementsTablePanel: Actualizando planta local de ${localSelectedStoreyId} a ${selectedStoreyId}`);
      setLocalSelectedStoreyId(selectedStoreyId);
    }
  }, [selectedStoreyId, localSelectedStoreyId]);

  // Efecto para expandir automáticamente SOLO cuando pasamos de 0 -> >0
  const prevCountRef = useRef<number>(0);
  useEffect(() => {
    const prev = prevCountRef.current;
    const curr = elements.length;
    if (prev === 0 && curr > 0 && !isExpanded) {
      logger.log('ELEMENTS_TABLE_PANEL', `📊 ElementsTablePanel: ${curr} elementos detectados por primera vez, expandiendo tabla automáticamente`);
      setIsExpanded(true);
    }
    prevCountRef.current = curr;
  }, [elements.length, isExpanded]);

  // Función helper para obtener el cómputo de una categoría desde el store
  const getCategoryComputo = (categoryId: string): string => {
    const category = getCategoryById(categoryId);
    return category && category.computo !== null ? `${category.computo}%` : '-';
  };

  // Función para obtener las opciones de fincas
  const getFincaOptions = () => {
    const fincas = getAllFincas();
    logger.log('ELEMENTS_TABLE_PANEL', '🏠 ElementsTablePanel: Fincas disponibles:', fincas.map(f => ({ numero: f.numeroFinca, nombre: f.nombre })));
    
    // Eliminar duplicados basándose en numeroFinca
    const fincasUnicas = fincas.filter((finca, index, self) => 
      index === self.findIndex(f => f.numeroFinca === finca.numeroFinca)
    );
    
    // Ordenar por numeroFinca
    const fincasOrdenadas = fincasUnicas.sort((a, b) => a.numeroFinca - b.numeroFinca);
    
    const opciones = [
      { numeroFinca: null, name: 'Sin asignar', nombreFinca: 'Sin asignar' },
      ...fincasOrdenadas.map(finca => ({
        numeroFinca: finca.numeroFinca,
        name: `Finca ${finca.numeroFinca}`,
        nombreFinca: finca.nombre || `Finca ${finca.numeroFinca}`,
        fincaCompleta: finca
      }))
    ];
    
    logger.log('ELEMENTS_TABLE_PANEL', '🏠 ElementsTablePanel: Opciones de fincas generadas:', opciones.map(o => ({ numero: o.numeroFinca, nombre: o.nombreFinca })));
    return opciones;
  };

  // Función para cambiar el nombre de finca de un elemento
  const changeElementFincaNombre = (elementId: string, newNombreFinca: string) => {
    const element = filteredAndSortedElements.find(el => el.id === elementId);
    if (!element || !mapService) return;

    const fincas = getAllFincas();
    
    // Si es "Sin asignar", limpiar la finca
    if (newNombreFinca === 'Sin asignar') {
      changeElementFinca(elementId, null);
      return;
    }

    // Buscar la finca por nombre personalizado o por el formato "Finca X"
    const finca = fincas.find(f => {
      const nombrePersonalizado = f.nombre || `Finca ${f.numeroFinca}`;
      return nombrePersonalizado === newNombreFinca;
    });

    if (finca) {
      logger.log('ELEMENTS_TABLE_PANEL', `🏠 ElementsTablePanel: Asignando elemento ${elementId} a finca ${finca.numeroFinca} (${finca.nombre || `Finca ${finca.numeroFinca}`})`);
      changeElementFinca(elementId, finca.numeroFinca);
    } else {
      logger.warn('ELEMENTS_TABLE_PANEL', `⚠️ ElementsTablePanel: No se encontró finca con nombre "${newNombreFinca}"`);
    }
  };

  // Función para cambiar la finca de un elemento
  const changeElementFinca = (elementId: string, newFincaNumero: number | null) => {
    try {
      const { updateElement } = useUnifiedElementsStore.getState();
      updateElement(elementId, { finca: newFincaNumero?.toString() || '' });
      
      const fincaName = newFincaNumero ? `Finca ${newFincaNumero}` : 'Sin asignar';
      addNotification({
        type: 'success',
        title: 'Finca actualizada',
        message: `Asignado a ${fincaName}`
      });
    } catch (error) {
      console.error('Error al cambiar finca:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la finca'
      });
    }
  };

  // Función para cambiar el nombre de un elemento
  const changeElementNombre = (elementId: string, newNombre: string) => {
    try {
      const { updateElement } = useUnifiedElementsStore.getState();
      updateElement(elementId, { nombre: newNombre });
      
      addNotification({
        type: 'success',
        title: 'Nombre actualizado',
        message: `Elemento renombrado a: ${newNombre || 'Sin nombre'}`
      });
    } catch (error) {
      console.error('Error al cambiar nombre:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar el nombre'
      });
    }
  };

  // Función para cambiar la categoría de un elemento
  const changeElementCategory = (elementId: string, newCategoryId: string) => {
    try {
      const { updateElement } = useUnifiedElementsStore.getState();
      updateElement(elementId, { category: newCategoryId });
      
      addNotification({
        type: 'success',
        title: 'Categoría actualizada',
        message: `Categoría actualizada a: ${getCategoryLabel(newCategoryId)}`
      });
    } catch (error) {
      console.error('Error al cambiar categoría:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la categoría'
      });
    }
  };

  // Función para cambiar la referencia catastral de un elemento (SIN NOTIFICACIÓN)
  const changeElementRefcat = (elementId: string, newRefcat: string) => {
    try {
      const { updateElement } = useUnifiedElementsStore.getState();
      updateElement(elementId, { refcat: newRefcat });
      // NO mostrar notificación aquí - solo se mostrará cuando se complete la edición
    } catch (error) {
      console.error('Error al cambiar RefCat:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la referencia catastral'
      });
    }
  };

  // Función para manejar cuando se completa la edición de RefCat
  const handleRefcatBlur = (elementId: string, newRefcat: string) => {
    try {
      // Asegurar que se guarde en el store
      const { updateElement } = useUnifiedElementsStore.getState();
      updateElement(elementId, { refcat: newRefcat });
      
      // Mostrar notificación solo cuando se complete la edición
      addNotification({
        type: 'success',
        title: 'RefCat actualizada',
        message: `Referencia catastral actualizada a: ${newRefcat || 'Vacía'}`
      });
    } catch (error) {
      console.error('Error al cambiar RefCat:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No se pudo cambiar la referencia catastral'
      });
    }
  };



  // Función para manejar el hover sobre un elemento de la tabla
  const handleElementHover = (elementId: string) => {
    logger.log('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: Hover sobre elemento ${elementId}`);
    
    const element = filteredAndSortedElements.find(el => el.id === elementId);
    if (!element) {
      logger.warn('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: No se encontró elemento con ID ${elementId}`);
      return;
    }
    
    if (!element.feature) {
      logger.warn('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: Elemento ${elementId} no tiene feature`);
      return;
    }
    
    if (!mapService) {
      logger.warn('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: No hay mapService disponible`);
      return;
    }
    
    const nombre = element.nombre || 'Sin nombre';
    logger.log('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: Mostrando etiqueta para elemento ${elementId}: ${nombre}`);
    
    // Mostrar etiqueta en el mapa
    try {
      mapService.showElementLabel(element.feature, nombre);
    } catch (error) {
      logger.error('ELEMENTS_TABLE_PANEL', 'Error al mostrar etiqueta:', error);
    }
  };

  // Función para manejar el fin del hover sobre un elemento de la tabla
  const handleElementHoverEnd = () => {
    logger.log('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: Fin de hover, ocultando etiqueta`);
    
    if (!mapService) {
      logger.warn('ELEMENTS_TABLE_PANEL', `🗺️ ElementsTablePanel: No hay mapService disponible`);
      return;
    }
    
    try {
      mapService.hideElementLabel();
    } catch (error) {
      logger.error('ELEMENTS_TABLE_PANEL', 'Error al ocultar etiqueta:', error);
    }
  };



  // Función para obtener las opciones de plantas
  const getStoreyOptions = () => {
    if (!mapService) return [];
    const gui = mapService.getGUI();
    if (!gui) return [];
    
    const storeyOptions = gui.getStoreyDropdownOptions();
    return storeyOptions;
  };



  // Función para refrescar estilos manualmente cuando no esté disponible refreshDrawLayerStyles
  const manualRefreshStyles = () => {
    if (!mapService) return;
    
    try {
      const layers = mapService.getLayers();
      if (!layers?.drawLayers) return;
      
      Object.entries(layers.drawLayers).forEach(([, layer]) => {
        const features = layer.getSource()?.getFeatures();
        if (features) {
          features.forEach(feature => {
            const type = feature.get('type');
            const categoryColor = getCategoryColor(type) || 'rgba(128, 128, 128, 0.7)';
            
            // Aplicar el nuevo estilo usando las importaciones del componente
            feature.setStyle(new Style({
              fill: new Fill({ color: categoryColor }),
              stroke: new Stroke({ color: 'black', width: 2 })
            }));
          });
        }
      });
      
      logger.log('ELEMENTS_TABLE_PANEL', '✅ ElementsTablePanel: Estilos refrescados manualmente');
    } catch (error) {
      logger.error('ELEMENTS_TABLE_PANEL', 'Error al refrescar estilos manualmente:', error);
    }
  };



  // Función para seleccionar un elemento en el mapa
  const selectElement = (elementId: string) => {
    const { setSelectedElementId } = useUnifiedElementsStore.getState();
    setSelectedElementId(elementId);
    
    // Buscar el elemento en el mapa y centrarlo
    const element = filteredAndSortedElements.find(el => el.id === elementId);
    if (element && mapService) {
      const map = mapService.getMap();
      if (map) {
        const geometry = element.geometry;
        if (geometry) {
          const extent = geometry.getExtent();
          map.getView().fit(extent, {
            padding: [50, 50, 50, 50],
            duration: 1000
          });
        }
      }
      
      // Resaltar el elemento seleccionado en el mapa
      highlightSelectedElement(element);
      
      addNotification({
        type: 'info',
        title: 'Elemento seleccionado',
        message: `${getCategoryLabel(element.category)} - ${element.area.toFixed(2)} m²`
      });
    }
  };

  // Función para resaltar el elemento seleccionado en el mapa
  const highlightSelectedElement = (element: any) => {
    if (!mapService) return;
    
    try {
      const layers = mapService.getLayers();
      if (!layers?.drawLayers) return;
      
      // Primero, quitar el resaltado de todos los elementos
      clearAllHighlights();
      
      // Buscar la feature correspondiente al elemento
      const layer = layers.drawLayers[element.storeyId];
      if (layer) {
        const features = layer.getSource()?.getFeatures();
        if (features) {
          // Buscar la feature que corresponde al elemento seleccionado
          // Usamos el índice del elemento en la lista para encontrar la feature correspondiente
          const elementIndex = parseInt(element.id.split('-')[1]);
          const feature = features[elementIndex];
          
          if (feature) {
            // Aplicar estilo de resaltado
            const type = feature.get('type');
            const categoryColor = getCategoryColor(type) || 'rgba(128, 128, 128, 0.7)';
            
            feature.setStyle(new Style({
              fill: new Fill({ color: categoryColor }),
              stroke: new Stroke({ 
                color: 'red', 
                width: 4 
              })
            }));
            
            logger.log('ELEMENTS_TABLE_PANEL', '✅ ElementsTablePanel: Elemento resaltado en el mapa');
          }
        }
      }
    } catch (error) {
      logger.error('ELEMENTS_TABLE_PANEL', 'Error al resaltar elemento:', error);
    }
  };

  // Función para quitar el resaltado de todos los elementos
  const clearAllHighlights = () => {
    if (!mapService) return;
    
    try {
      const layers = mapService.getLayers();
      if (!layers?.drawLayers) return;
      
      Object.entries(layers.drawLayers).forEach(([, layer]) => {
        const features = layer.getSource()?.getFeatures();
        if (features) {
          features.forEach(feature => {
            const type = feature.get('type');
            const categoryColor = getCategoryColor(type) || 'rgba(128, 128, 128, 0.7)';
            
            // Restaurar estilo normal
            feature.setStyle(new Style({
              fill: new Fill({ color: categoryColor }),
              stroke: new Stroke({ color: 'black', width: 2 })
            }));
          });
        }
      });
    } catch (error) {
      logger.error('ELEMENTS_TABLE_PANEL', 'Error al limpiar resaltados:', error);
    }
  };



  // Menú de informes
  const [isReportsMenuOpen, setIsReportsMenuOpen] = useState(false);

     // Función para generar informes
   const handleGenerateReports = async () => {
     if (!mapService) {
       addNotification({
         type: 'error',
         title: 'Error',
         message: 'El servicio de mapa no está disponible. Intenta recargar la página.'
       });
       return;
     }

     try {
       // Abrir/cerrar menú
       setIsReportsMenuOpen((v) => !v);

     } catch (error) {
       logger.error('ELEMENTS_TABLE_PANEL', 'Error al generar informe:', error);
       addNotification({
         type: 'error',
         title: 'Error',
         message: error instanceof Error ? error.message : 'Error al generar el informe'
       });
     }
   };

   // Función para exportar a CSV
   const handleExportCSV = () => {
     try {
       // Obtener todos los elementos de todas las plantas
       const allElements = elements;
       
       if (allElements.length === 0) {
         addNotification({
           type: 'warning',
           title: 'Sin datos',
           message: 'No hay elementos para exportar'
         });
         return;
       }

       // Función para obtener el nombre de la planta
       const getStoreyName = (storeyId: string): string => {
         if (!mapService) return storeyId || '';
         
         const gui = mapService.getGUI();
         if (!gui || !gui.getStoreyDropdownOptions) return storeyId || '';
         
         const storeyOptions = gui.getStoreyDropdownOptions();
         const storey = storeyOptions.find(option => option.id === storeyId);
         return storey?.name || storeyId || '';
       };

       // Crear encabezados CSV
       const headers = [
         'ID',
         'Nombre',
         'COD',
         'Categoría',
         'Superficie (m²)',
         'Cómputo (%)',
         'Finca',
         'Nombre de Finca',
         'RefCat',
         'Planta',
         'Nombre planta'
       ];

       // Crear filas de datos (usando ';' como separador y ',' como decimal)
       const csvRows = allElements.map(element => [
         element.id,
         element.nombre || '',
         element.category,
         getCategoryLabel(element.category),
         (element.area.toFixed(2)).replace('.', ','),
         getCategoryComputo(element.category),
         element.finca || '',
         getFincaNombreByNumero(element.finca),
         element.refcat || '',
         element.storeyId,
         getStoreyName(element.storeyId)
       ]);

       // Combinar encabezados y datos usando ';'
       const csvContent = [headers, ...csvRows]
         .map(row => row.map(field => `"${field}"`).join(';'))
         .join('\n');

       // Crear y descargar archivo
       const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
       const link = document.createElement('a');
       const url = URL.createObjectURL(blob);
       link.setAttribute('href', url);
       link.setAttribute('download', `elementos_${new Date().toISOString().split('T')[0]}.csv`);
       link.style.visibility = 'hidden';
       document.body.appendChild(link);
       link.click();
       document.body.removeChild(link);

       addNotification({
         type: 'success',
         title: 'Exportación exitosa',
         message: `Se exportaron ${allElements.length} elementos a CSV`
       });

       logger.log('ELEMENTS_TABLE_PANEL', `📊 CSV exportado con ${allElements.length} elementos`);
     } catch (error) {
       logger.error('ELEMENTS_TABLE_PANEL', 'Error al exportar CSV:', error);
       addNotification({
         type: 'error',
         title: 'Error de exportación',
         message: 'No se pudo exportar el archivo CSV'
       });
     }
   };

   // Función para importar desde CSV
   const handleImportCSV = async () => {
     try {
       // Crear input de archivo oculto
       const input = document.createElement('input');
       input.type = 'file';
       input.accept = '.csv';
       input.style.display = 'none';

       input.onchange = async (event) => {
         const file = (event.target as HTMLInputElement).files?.[0];
         if (!file) return;

         try {
           const text = await file.text();
           const lines = text.split('\n');
           
           if (lines.length < 2) {
             addNotification({
               type: 'error',
               title: 'Archivo inválido',
               message: 'El archivo CSV debe tener al menos encabezados y una fila de datos'
             });
             return;
           }

           // Parsear encabezados
           const headers = lines[0].split(';').map(h => h.replace(/"/g, '').trim());
           
           // Verificar campos obligatorios
           const idIndex = headers.findIndex(h => h === 'ID');
           const refcatIndex = headers.findIndex(h => h === 'RefCat');
           
           if (idIndex === -1 || refcatIndex === -1) {
             addNotification({
               type: 'error',
               title: 'Campos faltantes',
               message: 'El CSV debe contener las columnas "ID" y "RefCat"'
             });
             return;
           }

           // Parsear datos
           const csvData = lines.slice(1).filter(line => line.trim()).map(line => {
             const values = line.split(';').map(v => v.replace(/\"/g, '').trim());
             const rawNombre = headers.includes('Nombre') ? values[headers.findIndex(h => h === 'Nombre')] : undefined;
             const rawFincaNumero = headers.includes('Finca') ? values[headers.findIndex(h => h === 'Finca')] : undefined;
             const rawNombreFinca = headers.includes('Nombre de Finca') ? values[headers.findIndex(h => h === 'Nombre de Finca')] : undefined;
             return {
               id: values[idIndex],
               refcat: values[refcatIndex],
               // Solo propagar si vienen con contenido no vacío
               nombre: (rawNombre !== undefined && rawNombre.trim() !== '') ? rawNombre : undefined,
               category: headers.includes('COD') ? values[headers.findIndex(h => h === 'COD')] : undefined,
               finca: (rawFincaNumero !== undefined && rawFincaNumero.trim() !== '') ? rawFincaNumero : undefined,
               nombreFinca: (rawNombreFinca !== undefined && rawNombreFinca.trim() !== '') ? rawNombreFinca : undefined
             };
           });

           // Procesar importación
           let successCount = 0;
           let errorCount = 0;
           const errors: string[] = [];

           // 1) Preparar fincas: crear o renombrar según 'Nombre de Finca'
           const fincaAssignmentByElementId = new Map<string, string>();
           try {
             const fincasModule = await import('../../stores/fincasStore');
             const useFincasStoreRef = fincasModule.useFincasStore;
             const { getAllFincas, createFinca, createFincaWithNumber, updateFinca } = useFincasStoreRef.getState();

             for (const row of csvData) {
               // Solo actuar si hay nombre de finca proporcionado
               if (row.nombreFinca) {
                 const fincas = getAllFincas();
                 const fincaNumParsed = row.finca ? parseInt(row.finca as any, 10) : NaN;

                 if (isNaN(fincaNumParsed)) {
                   // Sin número de finca en CSV -> crear nueva con ese nombre y usar el número asignado
                   try {
                     const nueva = createFinca({ nombre: row.nombreFinca });
                     fincaAssignmentByElementId.set(row.id, nueva.numeroFinca.toString());
                   } catch (e) {
                     logger.warn('ELEMENTS_TABLE_PANEL', '⚠️ No se pudo crear la finca sin número:', e);
                   }
                 } else {
                   // Con número de finca -> renombrar existente o crearla con ese número
                   const existente = fincas.find((f: any) => f.numeroFinca === fincaNumParsed);
                   if (existente) {
                     try {
                       updateFinca(existente.id, { nombre: row.nombreFinca });
                       fincaAssignmentByElementId.set(row.id, existente.numeroFinca.toString());
                     } catch (e) {
                       logger.warn('ELEMENTS_TABLE_PANEL', '⚠️ No se pudo renombrar la finca existente:', e);
                     }
                   } else {
                     try {
                       const creada = createFincaWithNumber(fincaNumParsed, row.nombreFinca);
                       if (creada) {
                         fincaAssignmentByElementId.set(row.id, creada.numeroFinca.toString());
                       }
                     } catch (e) {
                       logger.warn('ELEMENTS_TABLE_PANEL', '⚠️ No se pudo crear la finca con número específico:', e);
                     }
                   }
                 }
               }
             }
           } catch (e) {
             logger.warn('ELEMENTS_TABLE_PANEL', '⚠️ No se pudo preparar fincas antes de asignar desde CSV:', e);
           }

           for (const row of csvData) {
             try {
               // Buscar elemento por ID
               const element = elements.find(el => el.id === row.id);
               
               if (element) {
                 // Actualizar RefCat y otros campos si están disponibles
                 const updates: any = { refcat: row.refcat };
                 
                 if (row.nombre !== undefined) {
                   updates.nombre = row.nombre;
                 }
                 if (row.category !== undefined) {
                   updates.category = row.category;
                 }

                 // Asignar finca en base al resultado de la fase previa
                 if (fincaAssignmentByElementId.has(row.id)) {
                   updates.finca = fincaAssignmentByElementId.get(row.id);
                 }

                 // Actualizar en el store
                 const { updateElement } = useUnifiedElementsStore.getState();
                 updateElement(row.id, updates);
                 
                 successCount++;
               } else {
                 errorCount++;
                 errors.push(`ID ${row.id} no encontrado`);
               }
             } catch (error) {
               errorCount++;
               errors.push(`Error procesando ID ${row.id}: ${error}`);
             }
           }

           // Mostrar resultados
           if (successCount > 0) {
             addNotification({
               type: 'success',
               title: 'Importación completada',
               message: `Se importaron ${successCount} elementos correctamente`
             });
           }

           if (errorCount > 0) {
             addNotification({
               type: 'warning',
               title: 'Importación parcial',
               message: `${errorCount} elementos no se pudieron importar. Revisa la consola para más detalles.`
             });
             
             logger.warn('ELEMENTS_TABLE_PANEL', `⚠️ Errores de importación:`, errors);
           }

           logger.log('ELEMENTS_TABLE_PANEL', `📊 CSV importado: ${successCount} exitosos, ${errorCount} errores`);

         } catch (error) {
           logger.error('ELEMENTS_TABLE_PANEL', 'Error al procesar CSV:', error);
           addNotification({
             type: 'error',
             title: 'Error de importación',
             message: 'No se pudo procesar el archivo CSV'
           });
         }
       };

       // Disparar selección de archivo
       document.body.appendChild(input);
       input.click();
       document.body.removeChild(input);

     } catch (error) {
       logger.error('ELEMENTS_TABLE_PANEL', 'Error al iniciar importación CSV:', error);
       addNotification({
         type: 'error',
         title: 'Error',
         message: 'No se pudo iniciar la importación CSV'
       });
     }
   };



  // Limpiar resaltado cuando se deseleccione un elemento
  useEffect(() => {
    if (!selectedElementId) {
      clearAllHighlights();
    }
  }, [selectedElementId]);

  // Inicializar localSelectedStoreyId si no está seleccionado y hay plantas disponibles
  useEffect(() => {
    if (!localSelectedStoreyId && mapService) {
      const storeyOptions = getStoreyOptions();
      if (storeyOptions.length > 0) {
        logger.log('ELEMENTS_TABLE_PANEL', '🏗️ ElementsTablePanel: Inicializando planta local seleccionada:', storeyOptions[0].id);
        setLocalSelectedStoreyId(storeyOptions[0].id);
        setSelectedStoreyId(storeyOptions[0].id); // También actualizar el store global
      }
    }
  }, [localSelectedStoreyId, mapService, setSelectedStoreyId]);

  // Sincronización: actualizar estado local cuando el store global cambie desde otros paneles
  useEffect(() => {
    if (selectedStoreyId && selectedStoreyId !== localSelectedStoreyId) {
      logger.log('ELEMENTS_TABLE_PANEL', '🔄 ElementsTablePanel: Sincronizando con store global:', selectedStoreyId);
      setLocalSelectedStoreyId(selectedStoreyId);
    }
  }, [selectedStoreyId, localSelectedStoreyId]);

  // Funciones para el redimensionamiento del panel
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    
    const windowHeight = window.innerHeight;
    const newHeight = windowHeight - e.clientY;
    
    // Limitar la altura mínima y máxima
    const minHeight = 200;
    const maxHeight = windowHeight * 0.8;
    
    if (newHeight >= minHeight && newHeight <= maxHeight) {
      setPanelHeight(newHeight);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Event listeners para el redimensionamiento
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);





  return (
    <div 
      ref={panelRef}
      className={`v2-elements-table-panel ${isExpanded ? 'expanded' : ''}`}
      style={isExpanded ? { height: `${panelHeight}px` } : {}}
    >
      {/* Botón de expansión */}
      <button 
        className="v2-elements-table-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? 'Ocultar tabla de elementos' : 'Mostrar tabla de elementos'}
      >
        <svg 
          className={`v2-elements-table-arrow ${isExpanded ? 'up' : 'down'}`} 
          viewBox="0 0 24 24"
        >
          <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
        </svg>
                 <span className="v2-elements-table-toggle-text">
           {isExpanded ? 'Ocultar' : 'Elementos'} ({filteredAndSortedElements.length})
         </span>
      </button>

      {/* Handle de redimensionamiento */}
      {isExpanded && (
        <div 
          className="v2-elements-table-resize-handle"
          onMouseDown={handleMouseDown}
          title="Arrastra para cambiar el tamaño"
        >
          <div className="v2-elements-table-resize-indicator">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M7,19H9V17H7V19M7,15H9V13H7V15M11,19H13V17H11V19M11,15H13V13H11V15M15,19H17V17H15V19M15,15H17V13H15V15M7,11H9V9H7V11M11,11H13V9H11V11M15,11H17V9H15V11M7,7H9V5H7V7M11,7H13V5H11V7M15,7H17V5H15V7Z" />
            </svg>
          </div>
        </div>
      )}

      {/* Contenido del panel */}
      {isExpanded && (
        <div className="v2-elements-table-content">
           <div className="v2-elements-table-header">
             <h3>Elementos Dibujados</h3>
             <div className="v2-elements-table-actions">
               {/* Desplegable de plantas */}
               <div className="v2-elements-table-filter">
                 <label className="v2-elements-table-filter-label">Planta:</label>
                 <select
                   className="v2-elements-table-filter-select"
                   value={localSelectedStoreyId}
                   onChange={(e) => {
                                           const newStoreyId = e.target.value;
                      logger.log('ELEMENTS_TABLE_PANEL', '🔄 ElementsTablePanel: Cambiando planta local a:', newStoreyId);
                      setLocalSelectedStoreyId(newStoreyId);
                      setSelectedStoreyId(newStoreyId); // También actualizar el store global
                      
                      // Cambiar la planta en el mapa también
                      const gui = mapService?.getGUI();
                      if (gui) {
                        gui.selectStoreyById(newStoreyId);
                        logger.log('ELEMENTS_TABLE_PANEL', '🔄 ElementsTablePanel: Planta cambiada en el mapa a:', newStoreyId);
                      }
                   }}
                 >
                   {getStoreyOptions().map(storey => (
                     <option key={storey.id} value={storey.id}>
                       {storey.name}
                     </option>
                   ))}
                 </select>
                               </div>
                 
                                   {/* Botón Exportar CSV */}
                  <button 
                    className="v2-elements-table-action-btn"
                    onClick={handleExportCSV}
                    title="Exportar a CSV"
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                  </button>

                                   {/* Botón Importar CSV */}
                  <button 
                    className="v2-elements-table-action-btn"
                    onClick={handleImportCSV}
                    title="Importar desde CSV"
                  >
                    <svg viewBox="0 0 24 24">
                      <path d="M19,4H5V6H19M19,15H15V21H9V15H5L12,8L19,15Z" />
                    </svg>
                  </button>

                 <button 
                   className="v2-elements-table-action-btn"
                   onClick={handleGenerateReports}
                   title="Generar Informes"
                 >
                   <svg viewBox="0 0 24 24">
                     <path d="M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8M16,19H8V17H16V19M19,12A1,1 0 0,1 18,13A1,1 0 0,1 17,12A1,1 0 0,1 18,11A1,1 0 0,1 19,12M16,16V14H8V16H6V13H18V16H16Z" />
                   </svg>
                 </button>
                 {isReportsMenuOpen && (
                   <div className="v2-reports-menu">
                     <button
                       className="v2-reports-menu-button"
                       onClick={async () => {
                         setIsReportsMenuOpen(false);
                         try {
                           addNotification({ type: 'info', title: 'Generando Informe', message: 'Generando informe de recintos...' });
                           const reportService = new RecintosReportService(mapService!);
                           const result = await reportService.generateRecintosReport();
                           addNotification({ type: 'success', title: 'Informe Generado', message: `Informe de recintos generado correctamente: ${result.fileName}` });
                         } catch (error) {
                           logger.error('ELEMENTS_TABLE_PANEL', 'Error al generar informe:', error);
                           addNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Error al generar el informe' });
                         }
                       }}
                     >
                       Informe de recintos
                     </button>
                     <button
                       className="v2-reports-menu-button"
                       onClick={async () => {
                         setIsReportsMenuOpen(false);
                         try {
                           addNotification({ type: 'info', title: 'Generando Informe', message: 'Generando informe notarial...' });
                           const { NotarialReportService } = await import('../../services/reports/notarialReportService');
                           const notarialService = new NotarialReportService(mapService!);
                           const result = await notarialService.generateNotarialReport();
                           addNotification({ type: 'success', title: 'Informe Generado', message: `Informe notarial generado correctamente: ${result.fileName}` });
                         } catch (error) {
                           logger.error('ELEMENTS_TABLE_PANEL', 'Error al generar informe notarial:', error);
                           addNotification({ type: 'error', title: 'Error', message: error instanceof Error ? error.message : 'Error al generar el informe notarial' });
                         }
                       }}
                     >
                       Informe notarial
                     </button>
                   </div>
                 )}
             </div>
           </div>

                     {filteredAndSortedElements.length === 0 ? (
             <div className="v2-elements-table-empty">
               <p>No hay elementos dibujados</p>
               <small>Dibuja elementos en el mapa para verlos aquí</small>
             </div>
           ) : (
            <div className="v2-elements-table-container">
              <table className="v2-elements-table">
                                                   <thead>
                                         <tr>
                       <th onClick={() => handleSort('elemId')} className="sortable">
                         ID {getSortIcon('elemId')}
                       </th>
                       <th onClick={() => handleSort('nombre')} className="sortable">
                         Nombre {getSortIcon('nombre')}
                       </th>
                       <th onClick={() => handleSort('catId')} className="sortable">
                         COD {getSortIcon('catId')}
                       </th>
                       <th onClick={() => handleSort('categoria')} className="sortable">
                         Categoría {getSortIcon('categoria')}
                       </th>
                       <th onClick={() => handleSort('area')} className="sortable">
                         Superficie (m²) {getSortIcon('area')}
                       </th>
                       <th onClick={() => handleSort('computo')} className="sortable">
                         Cómputo (%) {getSortIcon('computo')}
                       </th>
                       <th onClick={() => handleSort('finca')} className="sortable">
                         Finca {getSortIcon('finca')}
                       </th>
                       <th onClick={() => handleSort('nombreFinca')} className="sortable">
                         Nombre de Finca {getSortIcon('nombreFinca')}
                       </th>
                       <th onClick={() => handleSort('refcat')} className="sortable">
                         RefCat {getSortIcon('refcat')}
                       </th>
                     </tr>
                  </thead>
                                 <tbody>
                   {filteredAndSortedElements.map((element) => (
                                                                                                                   <tr 
                          key={element.id}
                          className={`v2-elements-table-row ${selectedElementId === element.id ? 'selected' : ''}`}
                          onClick={() => selectElement(element.id)}
                          onMouseEnter={() => handleElementHover(element.id)}
                          onMouseLeave={handleElementHoverEnd}
                                                  >
                           <td>{element.id}</td>
                         <td>
                           <input
                             className="v2-elements-table-nombre-input"
                             type="text"
                             value={element.nombre || ''}
                             placeholder="Introducir nombre..."
                             onChange={(e) => {
                               e.stopPropagation();
                               changeElementNombre(element.id, e.target.value);
                             }}
                             onClick={(e) => e.stopPropagation()}

                             onKeyDown={(e) => {
                               if (e.key === 'Enter') {
                                 e.preventDefault();
                                 e.currentTarget.blur();
                               }
                             }}
                           />
                         </td>
                         <td>{element.category}</td>
                         <td>
                           <div className="v2-elements-table-category">
                             <div 
                               className="v2-elements-table-color-indicator"
                               style={{ backgroundColor: getCategoryColor(element.category) }}
                             />
                             <select
                               className="v2-elements-table-category-select"
                               value={element.category}
                               onChange={(e) => {
                                 e.stopPropagation();
                                 changeElementCategory(element.id, e.target.value);
                               }}
                               onClick={(e) => e.stopPropagation()}
                             >
                               {useCategoriesStore.getState().categories.map((category) => (
                                 <option key={category.id} value={category.id}>
                                   {category.label}
                                 </option>
                               ))}
                             </select>
                           </div>
                         </td>
                       <td>{element.area.toFixed(2)}</td>
                       <td>{getCategoryComputo(element.category)}</td>
                                               <td>
                                                     {(() => {
                                                       const isZCE = element.category === 'ZC';
                                                                                                               logger.log('ELEMENTS_TABLE_PANEL', `🔍 ElementsTablePanel: Elemento ${element.id} - category: "${element.category}", isZCE: ${isZCE}`);
                                                       return isZCE ? (
                                                         <span className="v2-elements-table-no-finca">
                                                           No aplicable
                                                         </span>
                                                       ) : (
                                                         <select
                                                           className="v2-elements-table-finca-select"
                                                           value={element.finca || ''}
                                                           onChange={(e) => {
                                                             e.stopPropagation();
                                                             const newFincaNumero = e.target.value === '' ? null : parseInt(e.target.value);
                                                             changeElementFinca(element.id, newFincaNumero);
                                                           }}
                                                           onClick={(e) => e.stopPropagation()}
                                                         >
                                                           {getFincaOptions().map(finca => (
                                                             <option key={finca.numeroFinca || 'null'} value={finca.numeroFinca || ''}>
                                                               {finca.name}
                                                             </option>
                                                           ))}
                                                         </select>
                                                       );
                                                     })()}
                                               </td>
                                               <td>
                                                     {(() => {
                                                       const isZCE = element.category === 'ZC';
                                                       return isZCE ? (
                                                         <span className="v2-elements-table-no-finca">
                                                           No aplicable
                                                         </span>
                                                       ) : (
                                                         <select
                                                           className="v2-elements-table-finca-select"
                                                           value={getFincaNombreByNumero(element.finca)}
                                                           onChange={(e) => {
                                                             e.stopPropagation();
                                                             changeElementFincaNombre(element.id, e.target.value);
                                                           }}
                                                           onClick={(e) => e.stopPropagation()}
                                                         >
                                                           {getFincaOptions().map(finca => (
                                                             <option key={finca.numeroFinca || 'null'} value={finca.nombreFinca}>
                                                               {finca.nombreFinca}
                                                             </option>
                                                           ))}
                                                         </select>
                                                       );
                                                     })()}
                                               </td>
                                                                                               <td>
                                                  <input
                                                    className="v2-elements-table-refcat-input"
                                                    type="text"
                                                    value={element.refcat || ''}
                                                    placeholder="Introducir RefCat..."
                                                    onChange={(e) => {
                                                      e.stopPropagation();
                                                      // Solo actualizar el estado local, SIN notificación
                                                      changeElementRefcat(element.id, e.target.value);
                                                    }}
                                                    onBlur={(e) => {
                                                      e.stopPropagation();
                                                      // Al perder el foco, mostrar notificación y asegurar que se guarde
                                                      handleRefcatBlur(element.id, e.target.value);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        e.currentTarget.blur(); // Esto disparará onBlur
                                                      }
                                                    }}
                                                  />
                                                </td>
                     </tr>
                   ))}
                 </tbody>
              </table>
            </div>
          )}

                     <div className="v2-elements-table-summary">
             <span>Total: {filteredAndSortedElements.length} elementos</span>
             <span>Superficie total: {filteredAndSortedElements.reduce((sum: number, el: any) => sum + el.area, 0).toFixed(2)} m²</span>
           </div>
        </div>
      )}
    </div>
  );
};

export default ElementsTablePanel;
